/**
 * Global React error boundary.
 * Sprint 17 — Error Handling Polish.
 *
 * Catches unhandled JavaScript errors in the component tree and renders
 * a user-friendly fallback instead of a blank screen.
 *
 * NFR-USE-03 — Clear, jargon-free error messages.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message || 'An unexpected error occurred',
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base text-content-primary p-8 text-center font-sans">
          <div className="text-5xl mb-4">&#9888;</div>
          <h1 className="text-2xl font-semibold mb-3 text-content-primary">Something went wrong</h1>
          <p className="text-content-secondary mb-8 max-w-[30rem] leading-relaxed">
            The application encountered an unexpected problem. Your data is safe. Try reloading the page or going back
            to the home screen.
          </p>
          <div className="flex gap-4">
            <button
              onClick={this.handleReload}
              className="py-3 px-6 bg-action-primary text-white border-none rounded-lg text-base font-medium cursor-pointer"
            >
              Reload Page
            </button>
            <button
              onClick={this.handleGoHome}
              className="py-3 px-6 bg-transparent text-content-secondary border border-edge-default rounded-lg text-base font-medium cursor-pointer"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
